"use client"

import { useState, useEffect, useRef } from "react"
import { SendMark } from "@/components/ui/send-mark"
import Image from "next/image"
import Link from "next/link"
import { Input } from "@/components/ui/input"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  MapPin, ChevronLeft, ChevronRight, Loader2, UserCheck, UserPlus, Share2, Ban, MessageCircle,
} from "lucide-react"
import { toast } from "sonner"

// Shared talent-card primitives — the exact card + filter combobox + constants
// used on the owner Community/Network page, lifted here so the owner Hiring page
// can render the identical "source talent" feed (see components/network/talent-feed).

export const AVAILABILITY_LABELS: Record<string, string> = {
  full_time: "Full-time",
  part_time: "Part-time",
  weekdays_only: "Weekdays only",
  weekends_only: "Weekends only",
  flexible: "Flexible",
}

export const CLIENTELE_LABELS: Record<string, string> = {
  building: "Building clientele",
  established: "Established clientele",
  large_following: "Large following",
  walk_in_ready: "Walk In Ready",
}

// Static class strings (Tailwind can't see templated color names). Selected =
// solid filled pill + check; unselected = subtle outline keeping its category
// color — so active filters are unmistakable at a glance.
export const CHIP: Record<string, { on: string; off: string }> = {
  emerald: { on: "bg-emerald-500 text-white border-emerald-500", off: "bg-transparent text-emerald-600 border-emerald-500/40 hover:bg-emerald-500/10" },
  fuchsia: { on: "bg-fuchsia-500 text-white border-fuchsia-500", off: "bg-transparent text-fuchsia-600 border-fuchsia-500/40 hover:bg-fuchsia-500/10" },
  amber:   { on: "bg-amber-500 text-white border-amber-500",     off: "bg-transparent text-amber-600 border-amber-500/40 hover:bg-amber-500/10" },
  violet:  { on: "bg-violet-500 text-white border-violet-500",   off: "bg-transparent text-violet-600 border-violet-500/40 hover:bg-violet-500/10" },
  sky:     { on: "bg-sky-500 text-white border-sky-500",         off: "bg-transparent text-sky-600 border-sky-500/40 hover:bg-sky-500/10" },
}
export const CHIP_BASE = "text-[11px] font-light px-3 py-1 rounded-full border inline-flex items-center gap-1 transition-colors"

export const FILTER_AVAILABILITY = [
  { value: "full_time",     label: "Full-time" },
  { value: "part_time",     label: "Part-time" },
  { value: "weekdays_only", label: "Weekdays only" },
  { value: "weekends_only", label: "Weekends only" },
  { value: "flexible",      label: "Flexible" },
]

export const FILTER_CLIENTELE = [
  { value: "building",        label: "Building clientele" },
  { value: "established",     label: "Established clientele" },
  { value: "large_following", label: "Large following" },
  { value: "walk_in_ready",   label: "Walk In Ready" },
]

export const FILTER_PAYMENT = [
  { value: "booth_rental", label: "Booth rental" },
  { value: "commission",   label: "Commission" },
  { value: "both",         label: "Open to both" },
]

export interface BarberCard {
  id: string
  slug: string
  city: string | null
  state: string | null
  years_experience: number | null
  availability: string[] | null
  clientele_size: string[] | null
  payment_type_preference: string | null
  is_open_to_work: boolean
  is_content_creator: boolean
  profiles: { full_name: string; avatar_url: string }
  portfolio: { image_url: string }[]
  followers_count: number
}

export function CityFilterCombobox({
  value, onChange, cities,
}: {
  value: string
  onChange: (v: string) => void
  cities: string[]
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onDocClick)
    return () => document.removeEventListener("mousedown", onDocClick)
  }, [])

  const matches = value.trim()
    ? cities.filter(c => c.toLowerCase().includes(value.trim().toLowerCase()) && c.toLowerCase() !== value.trim().toLowerCase()).slice(0, 6)
    : cities.slice(0, 6)

  return (
    <div ref={wrapRef} className="relative">
      <Input
        placeholder="City"
        className="rounded-full text-sm h-9"
        value={value}
        onFocus={() => setOpen(true)}
        onChange={e => { onChange(e.target.value); setOpen(true) }}
      />
      {open && matches.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-background border border-border rounded-2xl shadow-lg overflow-hidden">
          <ul className="max-h-56 overflow-y-auto py-1">
            {matches.map(c => (
              <li key={c}>
                <button
                  type="button"
                  onClick={() => { onChange(c); setOpen(false) }}
                  className="w-full text-left text-sm px-4 py-2 hover:bg-muted transition-colors flex items-center gap-2"
                >
                  <MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="truncate">{c}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// ── Talent card: avatar+name+city header, 3-up portfolio, follow/share + badges below ──
export function TalentCard({
  barber, isSelf, inviteLoading, isFollowing, followLoading, onFollow, onMessage, onInvite,
}: {
  barber: BarberCard
  isSelf: boolean
  inviteLoading: boolean
  isFollowing: boolean
  followLoading: boolean
  onFollow: () => void
  onMessage: () => void
  onInvite: () => void
}) {
  const name = barber.profiles?.full_name || "Barber"
  const location = [barber.city, barber.state].filter(Boolean).join(", ")
  const previewImages = barber.portfolio.slice(0, 6)
  const [selfNudge, setSelfNudge] = useState<null | "message" | "inquire">(null)
  const portfolioRef = useRef<HTMLDivElement>(null)

  const handleShare = async () => {
    if (typeof window === "undefined") return
    const url = `${window.location.origin}/barbers/${barber.slug}`
    try {
      if (navigator.share) {
        await navigator.share({ title: `${name} | BOOTH`, url })
      } else {
        await navigator.clipboard.writeText(url)
        toast.success("Profile link copied")
      }
    } catch { /* user cancelled */ }
  }

  const handleMessageClick = () => {
    if (isSelf) {
      setSelfNudge("message")
      setTimeout(() => setSelfNudge(null), 1800)
      return
    }
    onMessage()
  }

  const handleInviteClick = () => {
    if (isSelf) {
      setSelfNudge("inquire")
      setTimeout(() => setSelfNudge(null), 1800)
      return
    }
    onInvite()
  }

  return (
    <div className="space-y-3 pb-4">
      {/* Header: avatar + name + city */}
      <Link href={`/barbers/${barber.slug}`} className="flex items-center gap-3 min-w-0 hover:opacity-80 transition-opacity">
        <Avatar className="w-11 h-11 border border-border flex-shrink-0">
          <AvatarImage src={barber.profiles?.avatar_url || ""} />
          <AvatarFallback className="text-sm bg-muted">{name.charAt(0)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="font-bold text-base truncate leading-tight">{name}</p>
          {location && (
            <p className="text-xs text-muted-foreground flex items-center gap-0.5 mt-0.5">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{location}</span>
            </p>
          )}
        </div>
      </Link>

      {/* Portfolio — 3-up horizontal scroller on every viewport, chevrons on
          hover for desktop. Touch-scroll handles mobile. */}
      {previewImages.length > 0 && (
        <div className="relative group">
          {previewImages.length > 3 && (
            <button
              type="button"
              onClick={() => portfolioRef.current?.scrollBy({ left: -260, behavior: "smooth" })}
              className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-background/95 border border-border shadow-md items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity -ml-3"
              aria-label="Previous"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          <div ref={portfolioRef} className="flex gap-2 overflow-x-auto scrollbar-hide snap-x snap-mandatory touch-pan-x pb-1">
            {previewImages.map((img, i) => (
              <Link
                key={i}
                href={`/barbers/${barber.slug}`}
                className="relative flex-shrink-0 w-[calc((100%-1rem)/3)] aspect-square overflow-hidden rounded-xl bg-muted snap-start"
              >
                <Image
                  src={img.image_url}
                  alt=""
                  fill
                  className="object-cover hover:scale-105 transition-transform duration-300"
                  sizes="(max-width: 640px) 33vw, 200px"
                />
              </Link>
            ))}
          </div>
          {previewImages.length > 3 && (
            <button
              type="button"
              onClick={() => portfolioRef.current?.scrollBy({ left: 260, behavior: "smooth" })}
              className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-background/95 border border-border shadow-md items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity -mr-3"
              aria-label="Next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Below the carousel: follow/share chip → XP years → Open to work →
          availability tags → clientele tags. */}
      <div className="flex flex-wrap items-center gap-2">
        {!isSelf && (
          <div className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary p-0.5">
            <button
              type="button"
              onClick={onFollow}
              disabled={followLoading}
              className="inline-flex items-center justify-center w-[26px] h-[26px] rounded-full text-emerald-500 hover:text-emerald-600 transition-colors disabled:opacity-40"
              aria-label={isFollowing ? "Unfollow" : "Follow"}
            >
              {followLoading
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : isFollowing
                  ? <UserCheck className="w-3.5 h-3.5" strokeWidth={2} />
                  : <UserPlus className="w-3.5 h-3.5" strokeWidth={2} />}
            </button>
            <span className="w-px h-4 bg-border" />
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center justify-center w-[26px] h-[26px] rounded-full text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Share profile"
            >
              <Share2 className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          </div>
        )}
        {barber.years_experience != null && (
          <span className="text-xs font-normal bg-muted text-foreground border border-border px-3 py-1 rounded-full whitespace-nowrap tabular-nums">
            {barber.years_experience}Y EXP
          </span>
        )}
        {barber.is_open_to_work && (
          <span className="text-[11px] font-light bg-emerald-50 text-emerald-700 border border-emerald-100 px-3 py-1 rounded-full">Open to work</span>
        )}
        {barber.is_content_creator && (
          <span className="text-[11px] font-light bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-100 px-3 py-1 rounded-full">Creator</span>
        )}
        {(barber.availability || []).map(a => (
          <span key={`avail-${a}`} className="text-[11px] font-light bg-amber-50 text-amber-700 border border-amber-100 px-3 py-1 rounded-full">
            {AVAILABILITY_LABELS[a] || a}
          </span>
        ))}
        {(barber.clientele_size || []).map(c => (
          <span key={`cli-${c}`} className="text-[11px] font-light bg-violet-50 text-violet-700 border border-violet-100 px-3 py-1 rounded-full">
            {CLIENTELE_LABELS[c] || c}
          </span>
        ))}
      </div>

      {/* Actions — flat icon+text links (no chrome) so Message and Inquire
          read as a quiet pair. Right-aligned with a touch of inset. */}
      <div className="flex justify-end items-center gap-5 pt-2 pr-2">
        <button
          onClick={handleMessageClick}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold transition-colors duration-300 ${
            selfNudge === "message"
              ? "text-red-600"
              : "text-muted-foreground hover:text-foreground"
          }`}
          aria-label={selfNudge === "message" ? "Can't message yourself" : "Message"}
        >
          {selfNudge === "message" ? (
            <>
              <Ban className="w-3.5 h-3.5" strokeWidth={2} />
              Can&apos;t message yourself
            </>
          ) : (
            <>
              <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.75} />
              Message
            </>
          )}
        </button>
        <button
          onClick={handleInviteClick}
          disabled={inviteLoading}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold transition-colors duration-300 disabled:opacity-40 ${
            selfNudge === "inquire" ? "text-red-600" : "text-emerald-700 hover:opacity-70"
          }`}
          aria-label={selfNudge === "inquire" ? "Can't inquire from yourself" : "Inquire"}
        >
          {selfNudge === "inquire" ? (
            <>
              <Ban className="w-3.5 h-3.5" strokeWidth={2} />
              Can&apos;t inquire from yourself
            </>
          ) : inviteLoading ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Sending…
            </>
          ) : (
            <>
              <SendMark size={14} />
              Inquire
            </>
          )}
        </button>
      </div>
    </div>
  )
}
